import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import ReportsListItem from '../../../ui/listItems/ReportsListItem';
import { trash2SVG, editSVG } from '../../../lib/allCustomSVGs';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import get_tara_notifications_api_call from '../../../lib/api/tara/notifications/get_tara_notifications_api_call';
import {
  eyeSVG,
  cursorSVG,
  dollarSVG,
  targetSVG,
  chartBarSVG,
  cart2SVG,
  bellSVG,
  cartAbandonedSVG,
  cartAddSVG,
  checkoutSVG,
  clockSVG,
  halfStarSVG,
  trendLineSVG as trendIcon,
} from '../../../lib/allCustomSVGs';
import get_graphs_api_call, {
  delete_graph_api_call,
  GraphApi,
} from '../../../lib/api/Reports/report_graph_api_call';

type Props = {
  refreshTrigger?: number;
  onEditGraph?: (graph: GraphApi) => void;
  setIntegrationsAndMetrics: (data: any[]) => void;
};

const ReportsGraphs = ({
  refreshTrigger,
  onEditGraph,
  setIntegrationsAndMetrics,
}: Props) => {
  const { t } = useTranslation();

  const [graphs, setGraphs] = useState<GraphApi[]>([]);
  const [deletingGraphId, setDeletingGraphId] = useState<string | null>(null);

  const [allNotifications, setAllNotifications] = useState<any[]>([]);
  const [logoMap, setLogoMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');

  // Token used only to force remount of child items after list changes,
  // so any cached base64 in ReportsListItem is cleared.
  const [cacheToken, setCacheToken] = useState<string>(() => `${Date.now()}`);

  useEffect(() => {
    setCacheToken(`${Date.now()}`);
  }, [refreshTrigger]);

  useEffect(() => {
    setCacheToken(`${Date.now()}`);
  }, [graphs]);

  const getIconByTitle = (title: string = '') => {
    const lower = title.toLowerCase();
    if (lower.includes('order')) return cart2SVG;
    if (lower.includes('view')) return eyeSVG;
    if (lower.includes('abandon')) return cartAbandonedSVG;
    if (lower.includes('add to cart')) return cartAddSVG;
    if (lower.includes('checkout')) return checkoutSVG;
    if (lower.includes('time')) return clockSVG;
    if (lower.includes('top')) return halfStarSVG;
    if (lower.includes('click')) return cursorSVG;
    if (lower.includes('engagement')) return cursorSVG;
    if (lower.includes('conversion')) return targetSVG;
    if (lower.includes('aov') || lower.includes('average order value'))
      return dollarSVG;
    if (lower.includes('cpm') || lower.includes('impression'))
      return chartBarSVG;
    if (lower.includes('revenue') || lower.includes('sales')) return dollarSVG;
    return bellSVG;
  };

  // Format helper: remove underscores and capitalize first letter
  const fmt = (val?: string) => {
    const s = (val ?? '').replace(/_/g, ' ').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  };

  // Sanitize {{short_code}} -> 'tara.insight.x'
  const sanitizeShortCode = (sc?: string | null) =>
    (sc || '').replace(/^\s*\{\{\s*/, '').replace(/\s*\}\}\s*$/, '');

  /* ---------- Build integrations+metrics options from notifications ---------- */
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading('pending');
      try {
        const allData = await get_tara_notifications_api_call();
        if (Array.isArray(allData) && allData.length > 0) {
          setAllNotifications(allData);
          setLoading('success');
        } else {
          setLoading('null');
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setLoading('error');
      }
    };
    fetchNotifications();
  }, []);

  useEffect(() => {
    const logos: Record<string, string> = {};
    allNotifications?.forEach((item) => {
      const slug = item?.mcp_info?.slug || item?.mcp;
      const icon = item?.mcp_info?.icon;
      if (slug && icon) logos[slug] = icon;
    });
    setLogoMap(logos);
  }, [allNotifications]);

  useEffect(() => {
    const grouped = allNotifications?.reduce(
      (acc: Record<string, any>, curr: any) => {
        const slug = curr?.mcp_info?.slug || curr?.mcp || 'other';
        const name = curr?.mcp_info?.name || curr?.mcp || 'Other';
        const icon = curr?.mcp_info?.icon || logoMap[slug] || '';

        if (!acc[slug]) {
          acc[slug] = {
            id: slug, // value for Integration select
            mainIcon: icon,
            mainTitle: name, // label for Integration select
            subTitle: '',
            items: [],
          };
        }

        acc[slug].items.push({
          id: curr.id,
          icon: getIconByTitle(curr.metric),
          title: curr.metric,
          value: sanitizeShortCode(curr.short_code) || curr.metric, // select value (short-code)
          subTitle: curr.description || '',
          tag: curr?.shop_config?.frequency
            ? curr.shop_config.frequency
            : 'none',
          isEnabled: curr.is_activated,
          shortCode: curr.short_code,
        });

        return acc;
      },
      {} as Record<string, any>
    );

    const groupedArr = Object.values(grouped || {});
    setIntegrationsAndMetrics(groupedArr);
  }, [allNotifications, logoMap, setIntegrationsAndMetrics]);

  /* ---------- Fetch graphs (real API) ---------- */
  useEffect(() => {
    const load = async () => {
      const list = await get_graphs_api_call();
      if (list) setGraphs(list);
    };
    load();
  }, [refreshTrigger]);

  const handleEditGraph = (graph: GraphApi) => onEditGraph?.(graph);

  const handleDeleteGraph = async (graphId: string) => {
    setDeletingGraphId(graphId);
    try {
      const ok = await delete_graph_api_call(graphId);
      if (ok) setGraphs((prev) => prev.filter((g) => g.id !== graphId));
    } catch (err) {
      console.error('Error deleting graph:', err);
    } finally {
      setDeletingGraphId(null);
    }
  };

  if (loading === 'pending') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <LoadingSpinner />
      </div>
    );
  }

  if (loading === 'error') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <NoData
          title={t('ErrorFetchingData', {
            defaultValue: 'Error fetching data',
          })}
          subTitle={t('SomethingWentWrong-msg', {
            defaultValue: 'Something went wrong',
          })}
        />
      </div>
    );
  }

  if (!graphs.length) {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <SettingsCommonCard
          mainTitle={t('YourGraphs', { defaultValue: 'Your graphs' })}
          subTitle={t('ConfiguredReportsGraphs', {
            defaultValue: 'Configured report graphs',
          })}
          titleFontSize='2rem'
          titleMarginBottom='0.5rem'
          childrenStyles={{ marginTop: '2rem' }}
        >
          <NoData
            title={t('NoGraphsFound', { defaultValue: 'No graphs found' })}
            subTitle={t('AddYourFirstGraph', {
              defaultValue: 'Add your first graph',
            })}
          />
        </SettingsCommonCard>
      </div>
    );
  }

  const graphKey = (g: GraphApi) =>
    [
      g.id,
      (g as any).updated_at,
      (g as any).updatedAt,
      (g as any).modified_at,
      (g as any).modifiedAt,
      (g as any).version,
      cacheToken,
    ]
      .filter(Boolean)
      .join('-');

  return (
    <div className='containerWithPaddingTopOnMobile'>
      <SettingsCommonCard
        mainTitle={t('YourGraphs', { defaultValue: 'Your graphs' })}
        subTitle={t('ConfiguredReportsGraphs', {
          defaultValue: 'Configured report graphs',
        })}
        titleFontSize='2rem'
        titleMarginBottom='0.5rem'
        childrenStyles={{
          marginTop: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {graphs.map((graph) => {
          const isDeleting = deletingGraphId === graph.id;

          const descr = `${fmt(graph.tara_mcp?.slug)} • ${
            graph.metrics?.length ?? 0
          } ${t('metrics', { defaultValue: 'metrics' })} • ${fmt(
            graph.granularity
          )}:${fmt(graph.date_range)}`;

          return (
            <ReportsListItem
              key={graphKey(graph)}
              id={graph.id}
              icon={trendIcon}
              iconColor='#20f37f'
              title={graph.name}
              subTitle={descr}
              shortCode={graph.placeholder ?? undefined}
              onConfigClick={() => !isDeleting && handleDeleteGraph(graph.id)}
              configIcon={isDeleting ? null : trash2SVG}
              configIconColor='#FF4B4B'
              {...(isDeleting && { configLoading: true })}
              onEditClick={() => !isDeleting && handleEditGraph(graph)}
              editIcon={editSVG}
              editIconColor='#6B7280'
              editIconSize='1.4rem'
              // keep allowed DownloadOptions only
              downloadOptions={{ filename: 'report.png', height: 800 }}
            />
          );
        })}
      </SettingsCommonCard>
    </div>
  );
};

export default ReportsGraphs;
